import axios from 'axios';

import Endpoints from '~/constants/endpoints';
import { MarketplaceSortOption } from '~/models/market/MarketplaceSortOption';
import { formatUrl } from '~/utils/formatUrl';
import type { MarketplaceLastTradeResponse } from '~/models/market/MarketplaceLastTradeResponse';
import { httpRequest, MakeMangedRequestOptions } from '~/networking';
import type { MarketplaceBuySellOrderResponse } from '~/models/market/MarketplaceBuySellOrderResponse';
import type { MarketplaceDomainProduct } from '~/models/market/MarketplaceDomainProduct';

/** Get last trade price for a set of productIDs. */
export async function getLastTradePrice(jwt: string, productID: Number | Number[]) {
    const headers = {
        Authorization: `Bearer ${jwt}`,
        'Accept-Language': 'en'
    };
    const queryParams = productID.toString();

    return axios
        .get<MarketplaceLastTradeResponse>(formatUrl(Endpoints.GET_LAST_TRADE_PRICE, queryParams), {
            headers
        })
        .then((res) => {
            return res.data;
        });
}

export interface GetMarketDirectoryFilters {
    name?: string;
    collectionId?: string;
    lwin11s?: string[];
    status?: string;
}

/** Get all wine in the marketplace with open buy and sell orders. */
export const getMarketDirectory =
    ({ getHeaders, getRequestUrl }: MakeMangedRequestOptions) =>
    async (
        pageSize: number,
        page: number,
        sorting: MarketplaceSortOption,
        productCategory?: string,
        filters?: GetMarketDirectoryFilters
    ) => {
        const headers = getHeaders(true);
        const query = new URLSearchParams();
        query.append('pageSize', pageSize.toString());
        query.append('page', page.toString());
        query.append('sorting', sorting);
        query.append('productCategory', productCategory || '');
        if (filters) {
            if (filters.name) {
                const name = filters.name?.trim();
                if (name && name.length > 2) {
                    query.append('name', name);
                }
            }
            if (filters.lwin11s && filters.lwin11s?.length > 0) {
                query.append('productCodes', filters.lwin11s.join(','));
            }
            if (filters.collectionId) {
                query.append('collection', filters.collectionId);
            }
            if (filters.status) {
                query.append('status', filters.status);
            }
        }
        const url = getRequestUrl(`/v1/markets/directory?${query.toString()}`);
        return httpRequest.get<MarketplaceBuySellOrderResponse>(url, { headers });
    };

/** Get info on market products. */
export const getMarketProductInfo =
    ({ getHeaders, getRequestUrl }: MakeMangedRequestOptions) =>
    async (productIds: number[]) => {
        const headers = getHeaders(true);
        const query = new URLSearchParams();
        query.append('productIds', productIds.join(','));
        const url = getRequestUrl(`/v1/products/info?${query.toString()}`);
        return httpRequest.get<MarketplaceDomainProduct[]>(url, { headers });
    };
